<template>
    <div
        :id="keywordListId"
        class="keyword-list-card"
    >
        <div class="kw-col-head">
            <span>{{ actionsHeadLeft }}</span>
            <span class="text-right">{{ $t('keywords.list.header.forAiLucy') }}</span>
        </div>
        <div
            class="kw-scroll"
            :style="scrollStyle"
        >
            <div
                v-for="(keyword, index) in keywordsOrdered"
                :key="keywordType + index"
                class="kw-row"
                :class="{'kw-row--main': keyword.keyword === article.selectedKeyword}"
                @click="handleRowClick(keyword.keyword)"
            >
                <div class="kw-row__mid">
                    <div class="kw-row__phrase">
                        {{ keyword.keyword }}
                        <span
                            v-if="serpBadges(keyword).length"
                            class="keyword-serp-badges"
                        >
                            <v-tooltip
                                v-for="badge in serpBadges(keyword)"
                                :key="badge"
                                bottom
                            >
                                <template #activator="{ on }">
                                    <span
                                        class="keyword-serp-badge"
                                        v-on="on"
                                    >
                                        <v-icon
                                            x-small
                                            color="primary"
                                        >
                                            {{ serpIcon(badge) }}
                                        </v-icon>
                                        <span
                                            v-if="badge === 'ai_overview'"
                                            class="keyword-serp-aio"
                                        >AI Overview</span>
                                    </span>
                                </template>
                                {{ $t('keywords.list.serp.' + badge) }}
                            </v-tooltip>
                        </span>
                        <v-icon
                            v-if="keyword.keyword === article.selectedKeyword"
                            class="ml-1 selected-keyword-check"
                            size="18"
                            color="selectedKeywordIcon"
                        >
                            check_circle
                        </v-icon>
                    </div>
                    <div class="kw-row__meta">
                        <span
                            v-if="rowSource(keyword)"
                            class="kw-src"
                            :class="'kw-src--' + sourceChipClass(rowSource(keyword))"
                        >
                            <WorkspaceAbbrTip
                                v-if="sourceAbbrTip(rowSource(keyword))"
                                :label="sourceChipLabel(rowSource(keyword))"
                                :text="sourceAbbrTip(rowSource(keyword))"
                            />
                            <template v-else>{{ sourceChipLabel(rowSource(keyword)) }}</template>
                        </span>
                        <span
                            v-if="rowVolume(keyword)"
                            class="kw-row__vol"
                        >{{ rowVolume(keyword) }}</span>
                    </div>
                    <div
                        v-if="answerHint(keyword)"
                        class="keyword-answer-hint"
                    >
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <span
                                    class="keyword-answer-hint__text"
                                    v-on="on"
                                >{{ answerHintPreview(keyword) }}</span>
                            </template>
                            {{ answerHint(keyword) }}
                        </v-tooltip>
                    </div>
                    <div
                        v-if="!readOnly && keyword.keyword === currentSelectedKeyword && keyword.keyword !== article.keyword"
                        class="kw-row__main-pick"
                        @click.stop
                    >
                        <v-btn
                            color="accent"
                            x-small
                            depressed
                            :loading="loading"
                            class="text-none"
                            @click="$emit('change-main-keyword', keyword.keyword)"
                        >
                            {{ $t('keywords.list.chooseNewKeyword') }}
                        </v-btn>
                    </div>
                </div>
                <div
                    class="kw-row__controls"
                    @click.stop
                >
                    <KeywordLucyRowControls
                        :phrase="keyword.keyword"
                        :keyword-type="keywordType"
                        :is-main-keyword="keyword.keyword === article.selectedKeyword || keyword.keyword === article.keyword"
                        :read-only="readOnly"
                        :selected-for-ai="selectedForAiLocal"
                        @update:selectedForAi="onLucySelectionUpdate"
                    />
                </div>
            </div>
            <div
                v-if="!keywords || keywords.length === 0"
                class="kw-row kw-row--empty"
            >
                {{ $t('keywords.list.empty') }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {KeywordType, KeywordTypeLangPaths} from '@/enum/keyword_type';
    import {Article, KeywordForAi} from "@/model/Article";
    import KeywordLucyRowControls from '@/components/keywords/KeywordLucyRowControls.vue';
    import WorkspaceAbbrTip from '@/components/keywords/WorkspaceAbbrTip.vue';
    import {
        keywordAiVolume,
        keywordAnswerHint,
        SERP_FEATURE_ICONS,
        serpFeatureBadges,
        type SerpFeatureBadge,
    } from '@/service/serpFeatures';

    const VISIBLE_KEYWORD_ROWS = 10;

    type KeywordListRow = Record<string, unknown>;
    const SOURCE_CHIP: Record<string, string> = {
        paa: 'paa',
        mentions: 'chat',
        lsi: 'lsi',
        organicFaq: 'top',
        autocomplete: 'ac',
        pas: 'pas',
        related: 'top',
        labs: 'top',
    };

    export default Vue.extend({
        name: 'KeywordsList',
        components: {KeywordLucyRowControls, WorkspaceAbbrTip},
        props: {
            keywordType: {
                type: Number,
                default: 0
            },
            keywords: {
                type: Array as PropType<KeywordListRow[]>,
                default: () => []
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            article: {
                type: Article,
                default: new Article
            },
            currentSelectedKeyword: {
                type: String,
                default: null
            },
            selectedForAi: {
                type: Array as PropType<KeywordForAi[]>,
                default: () => []
            }
        },
        data: () => ({
            KeywordTypeLangPaths: KeywordTypeLangPaths,
            loading: false,
            selectedForAiLocal: [] as (KeywordForAi | string)[],
            tableViewportHeight: undefined as number | undefined,
            viewportObserver: null as ResizeObserver | null
        }),
        computed: {
            keywordListId(): string {
                return KeywordTypeLangPaths[this.keywordType].split('.')[2];
            },
            isQuestionList(): boolean {
                return this.keywordType === KeywordType.QUESTION;
            },
            isVariationList(): boolean {
                return this.keywordType === KeywordType.VARIATION;
            },
            actionsHeadLeft(): string {
                if (this.isQuestionList) {
                    return String(this.$t('keywords.list.workspace.colQuestion'));
                }
                if (this.isVariationList) {
                    return String(this.$t('keywords.list.workspace.colTerm'));
                }
                return String(this.$t('keywords.list.workspace.colPhrase'));
            },
            actionsHeadRight(): string {
                return '';
            },
            sharePreview(): boolean {
                return this.$store.getters.sharePreview;
            },
            scrollStyle(): Record<string, string> {
                if (!this.tableViewportHeight) {
                    return {};
                }
                return {maxHeight: this.tableViewportHeight + 'px'};
            },
            keywordsOrdered(): KeywordListRow[] {
                const clone = this.keywords?.slice();
                if (clone) {
                    return clone.sort((a: KeywordListRow, b: KeywordListRow) => {
                        if (this.isQuestionList) {
                            const aiDiff = (keywordAiVolume(b) || 0) - (keywordAiVolume(a) || 0);
                            if (aiDiff) {
                                return aiDiff;
                            }
                        }
                        const aInfo = a.keyword_info as Record<string, unknown> | undefined;
                        const bInfo = b.keyword_info as Record<string, unknown> | undefined;
                        if (aInfo && bInfo) {
                            return +this.averageSearches(bInfo) - +this.averageSearches(aInfo);
                        } else if (a.serp_info) {
                            return 1;
                        } else {
                            return -1;
                        }
                    });
                }
                return [];
            }
        },
        watch: {
            selectedForAi: {
                immediate: true,
                handler: function () {
                    this.selectedForAiLocal = this.selectedForAi;
                }
            },
            selectedForAiLocal: function () {
                this.$emit('update:selectedForAi', this.selectedForAiLocal);
            },
            keywords: {
                deep: true,
                handler: function () {
                    this.$nextTick(this.measureTableViewport);
                }
            }
        },
        mounted: function () {
            this.bindViewportTracking();
            this.$nextTick(this.measureTableViewport);
        },
        beforeDestroy: function () {
            this.unbindViewportTracking();
        },
        deactivated: function () {
            this.loading = false;
        },
        methods: {
            bindViewportTracking: function (): void {
                if (typeof window === 'undefined') {
                    return;
                }
                window.addEventListener('resize', this.measureTableViewport);
                if (typeof ResizeObserver === 'undefined') {
                    return;
                }
                this.viewportObserver = new ResizeObserver(() => {
                    this.measureTableViewport();
                });
                this.$nextTick(() => {
                    const root = this.scrollElement();
                    if (root && this.viewportObserver) {
                        this.viewportObserver.observe(root);
                    }
                });
            },
            unbindViewportTracking: function (): void {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('resize', this.measureTableViewport);
                }
                if (this.viewportObserver) {
                    this.viewportObserver.disconnect();
                    this.viewportObserver = null;
                }
            },
            scrollElement: function (): HTMLElement | null {
                const root = this.$el as HTMLElement | undefined;
                if (!root || typeof root.querySelector !== 'function') {
                    return null;
                }
                return root.querySelector('.kw-scroll');
            },
            measureTableViewport: function (): void {
                const keywordCount = this.keywordsOrdered.length;
                if (keywordCount <= VISIBLE_KEYWORD_ROWS) {
                    if (this.tableViewportHeight !== undefined) {
                        this.tableViewportHeight = undefined;
                    }
                    return;
                }
                const scroller = this.scrollElement();
                if (!scroller) {
                    return;
                }
                const rows = scroller.querySelectorAll('.kw-row');
                if (rows.length === 0) {
                    return;
                }
                const visibleCount = Math.min(VISIBLE_KEYWORD_ROWS, rows.length);
                let rowsHeight = 0;
                for (let i = 0; i < visibleCount; i++) {
                    rowsHeight += Math.ceil((rows[i] as HTMLElement).getBoundingClientRect().height);
                }
                if (rowsHeight === 0) {
                    return;
                }
                if (rowsHeight !== this.tableViewportHeight) {
                    this.tableViewportHeight = rowsHeight;
                }
            },
            sourceChipClass: function (source: string): string {
                return SOURCE_CHIP[source] || 'top';
            },
            rowSource: function (keyword: Record<string, unknown>): string {
                const tagged = String(keyword.briefSource || '').trim();
                if (tagged) {
                    return tagged;
                }
                if (this.isQuestionList) {
                    return 'paa';
                }
                if (this.isVariationList) {
                    return 'lsi';
                }
                if (this.keywordType === KeywordType.LONG_TAIL) {
                    return 'labs';
                }
                return 'paa';
            },
            sourceChipLabel: function (source: string): string {
                if (source === 'pas') {
                    return 'PAS';
                }
                if (source === 'lsi') {
                    return 'LSI';
                }
                return String(this.$t('aiBrief.sources.' + source));
            },
            averageSearches: function (keywordInfo: Record<string, unknown> | undefined): string {
                if (!keywordInfo) {
                    return '';
                }
                const monthly = keywordInfo.monthly_searches;
                if (!Array.isArray(monthly) || monthly.length === 0) {
                    const volume = Number(keywordInfo.search_volume);
                    return volume > 0 ? volume.toLocaleString(this.$i18n.locale) : '';
                }
                const avg = Math.round(monthly
                    .map((row: {search_volume?: number}) => row.search_volume || 0)
                    .reduce((a: number, b: number) => a + b, 0) / monthly.length);
                return avg > 0 ? avg.toLocaleString(this.$i18n.locale) : '';
            },
            formatAiVolume: function (keyword: Record<string, unknown>): string {
                const volume = keywordAiVolume(keyword);
                return volume === null ? '' : volume.toLocaleString(this.$i18n.locale);
            },
            rowVolume: function (keyword: Record<string, unknown>): string {
                const parts: string[] = [];
                const searches = this.averageSearches(keyword.keyword_info as Record<string, unknown> | undefined);
                if (searches) {
                    parts.push(searches);
                }
                const kd = this.keywordDifficulty(keyword);
                if (kd) {
                    parts.push('KD ' + kd);
                }
                const ai = this.formatAiVolume(keyword);
                if (ai) {
                    parts.push('AI ' + ai);
                }
                return parts.join(' · ');
            },
            serpBadges: function (keyword: Record<string, unknown>): SerpFeatureBadge[] {
                return serpFeatureBadges(keyword);
            },
            serpIcon: function (badge: SerpFeatureBadge): string {
                return SERP_FEATURE_ICONS[badge];
            },
            answerHint: function (keyword: Record<string, unknown>): string {
                return keywordAnswerHint(keyword);
            },
            answerHintPreview: function (keyword: Record<string, unknown>): string {
                const hint = keywordAnswerHint(keyword);
                if (hint.length <= 90) {
                    return hint;
                }
                return hint.slice(0, 90).replace(/\s+\S*$/, '') + '…';
            },
            keywordDifficulty: function (keyword: Record<string, unknown>): string | null {
                const props = keyword.keyword_properties as {keyword_difficulty?: string} | undefined;
                if (props) {
                    return props.keyword_difficulty || null;
                } else {
                    return null;
                }
            },
            handleRowClick: function (keyword: string): void {
                this.$emit('update:currentSelectedKeyword', keyword);
            },
            onLucySelectionUpdate(rows: KeywordForAi[]): void {
                this.selectedForAiLocal = rows;
            },
            sourceAbbrTip(source: string): string {
                const key = source === 'pas' ? 'PAS' : source === 'lsi' ? 'LSI' : source;
                const path = 'keywords.lucy.abbr.' + key;
                const tip = this.$t(path);
                return tip === path ? '' : String(tip);
            },
        }
    });
</script>

<style scoped lang="sass">
.keyword-list-card
    overflow: hidden

.kw-col-head
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    padding: 6px 10px
    font-size: 10px
    font-weight: 800
    letter-spacing: .04em
    text-transform: uppercase
    color: rgba(255, 255, 255, .85)
    background: linear-gradient(120deg, #0c98f5 0%, #0566c3 100%)

.kw-scroll
    overflow-y: auto
    overscroll-behavior: contain

.kw-row
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    align-items: start
    padding: 8px 10px
    border-bottom: 1px solid #edf0f5
    background: var(--v-surface-base)
    cursor: pointer

    &:hover
        background: #f7fbff

.kw-row--main
    background: #333
    color: #fff

    &:hover
        background: #2a2a2a
        color: #fff

.kw-row--empty
    cursor: default
    font-size: 13px
    color: #6b7385
    grid-template-columns: 1fr

.kw-row__lucy
    display: flex
    align-items: center
    gap: 4px
    padding-top: 2px
    cursor: pointer
    font-size: 10px
    font-weight: 700
    color: #6b7385
    letter-spacing: .02em
    user-select: none

.kw-row__lucy-box ::v-deep .v-input--selection-controls__input
    margin-right: 4px

.kw-row__lucy-lbl
    font-size: 10px
    font-weight: 700
    line-height: 1.2
    letter-spacing: .02em
    color: #6b7385

.kw-row--main .kw-row__lucy,
.kw-row--main .kw-row__lucy-lbl
    color: rgba(255, 255, 255, .8)

.kw-row__mid
    min-width: 0

.kw-row__phrase
    font-size: 13px
    font-weight: 500
    line-height: 1.35

.kw-row__meta
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 4px
    margin-top: 4px

.kw-src
    display: inline-flex
    align-items: center
    height: 18px
    padding: 0 6px
    border-radius: 999px
    font-size: 10px
    font-weight: 800
    letter-spacing: .02em
    border: 1px solid #d5dce8
    color: #4a5368
    background: #fff
    text-transform: none

.kw-src--paa
    color: var(--sw-primary-fill)
    border-color: #9ec9ee

.kw-src--chat
    color: #8e3d82
    border-color: #e4b7dd
    background: #fbf4fa

.kw-src--lsi
    color: #8e3d82
    border-color: #cd7ec2
    background: #f6e8f3

.kw-src--top
    color: #445
    border-color: #cfd6e4

.kw-src--ac
    color: #445
    border-color: #c9d4c0
    background: #f4f8ee

.kw-src--pas
    color: var(--sw-primary-fill)
    border-color: #b7d4c8
    background: #eef7f2

.kw-row--main .kw-src
    color: rgba(255, 255, 255, .82)
    border-color: rgba(255, 255, 255, .3)
    background: transparent

.kw-row__vol
    font-size: 11px
    color: #6b7385
    white-space: nowrap
    margin-left: 4px

.kw-row--main .kw-row__vol
    color: rgba(255, 255, 255, .82)

.kw-row__controls
    display: flex
    flex-direction: column
    align-items: flex-end

.keyword-answer-hint
    margin-top: 3px
    font-size: 12px
    line-height: 1.35
    color: #6b7385

.kw-row--main .keyword-answer-hint
    color: rgba(255, 255, 255, .75)

.keyword-answer-hint__text
    display: -webkit-box
    -webkit-line-clamp: 1
    -webkit-box-orient: vertical
    overflow: hidden

.keyword-serp-badges
    display: inline-flex
    align-items: center
    gap: 2px
    margin-left: 4px
    vertical-align: middle

.keyword-serp-badge
    display: inline-flex
    align-items: center
    gap: 1px
    cursor: help

.keyword-serp-aio
    font-size: 9px
    font-weight: 800
    letter-spacing: .01em
    text-transform: none

.kw-row__main-pick
    margin-top: 6px

@media (max-width: 959px)
    .kw-col-head,
    .kw-row
        grid-template-columns: 28px minmax(0, 1fr)

    .kw-row__lucy-lbl
        display: none

.theme--dark
    .kw-row
        border-bottom-color: rgba(255, 255, 255, .08)

        &:hover
            background: rgba(8, 135, 220, .12)

    .kw-row__vol,
    .kw-row__lucy,
    .kw-row__lucy-lbl,
    .keyword-answer-hint
        color: #a8b0c2

    .kw-pill--h2
        background: rgba(8, 135, 220, .22) !important
        color: #81C2ED !important
</style>

<style lang="sass">
.inverted-checkbox .theme--dark.v-icon,
.inverted-checkbox .theme--light.v-icon
    color: var(--v-selectedKeywordColor-base)

.kw-row--main
    .v-input--selection-controls__input
        color: #fff
</style>
